import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { X, RefreshCw, Edit3, Check } from 'lucide-react';
import { normalizeNoteContent } from './NotesModal';

const formatDate = (value) => {
  if (!value) return 'N/A';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const formatDateOnly = (value) => {
  if (!value) return 'Not set';
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value || 'Not set';
    }
    return date.toLocaleDateString();
  } catch {
    return value || 'Not set';
  }
};

const normalizeDateInputValue = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return trimmed;
    }
    return trimmed.slice(0, 10);
  }
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toISOString().slice(0, 10);
  } catch {
    return '';
  }
};


const CONTACT_STATUS_CHOICES = [
  'AM Client',
  'Requested Bid',
  'SBO - AM Client',
  'GateKeeper AM',
  'GateKeeper SBO',
  'None',
  'Procurement',
  'Award Winner',
  'SBO Client'
];

const parseContactStatusList = (value) => {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) {
    return value.map((item) => (typeof item === 'string' ? item.trim() : String(item))).filter(Boolean);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed || trimmed.toLowerCase() === 'null') return [];

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => (typeof item === 'string' ? item.trim() : String(item))).filter(Boolean);
      }
      if (typeof parsed === 'string') {
        return parsed.trim() ? [parsed.trim()] : [];
      }
    } catch (error) {
      // Ignore parse errors and fallback
    }

    if (trimmed.includes(',')) {
      return trimmed
        .split(',')
        .map((item) => item.replace(/["']/g, '').trim())
        .filter(Boolean);
    }

    if ((trimmed.startsWith('"') && trimmed.endsWith('"')) || (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
      return [trimmed.slice(1, -1).trim()].filter(Boolean);
    }

    return [trimmed];
  }
  return [String(value).trim()];
};

const normalizeContactStatusInput = (value) => {
  const trimmed = (value || '').trim();
  if (!trimmed) return null;

  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed)) {
      const cleaned = parsed
        .map((item) => (typeof item === 'string' ? item.trim() : String(item)))
        .filter(Boolean);
      return cleaned.length ? JSON.stringify(cleaned) : null;
    }
    if (typeof parsed === 'string') {
      return parsed.trim() || null;
    }
  } catch (error) {
    // fall through
  }

  const list = trimmed
    .split(',')
    .map((item) => item.replace(/["']/g, '').trim())
    .filter(Boolean);

  if (!list.length) {
    return trimmed;
  }

  return list.length === 1 ? list[0] : JSON.stringify(list);
};

const parseTagsArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map((tag) => (typeof tag === 'string' ? tag.trim() : String(tag))).filter(Boolean);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.map((tag) => (typeof tag === 'string' ? tag.trim() : String(tag))).filter(Boolean);
      }
    } catch (error) {
      // ignore parsing error and fall through
    }
    return trimmed
      .split(',')
      .map((tag) => tag.replace(/["']/g, '').trim())
      .filter(Boolean);
  }
  return [];
};

const serializeTagsArray = (tags) => {
  if (!Array.isArray(tags)) return null;
  const cleaned = tags.map((tag) => tag.trim()).filter(Boolean);
  if (!cleaned.length) return null;
  return JSON.stringify(cleaned);
};

const formatCapitalizedName = (value = '') => {
  if (!value) return '';
  return value
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const getAssignmentDisplayName = (contact = {}) => {
  if (!contact) return 'Unassigned';
  if (typeof contact.producer_name === 'object' && contact.producer_name !== null) {
    const capitalizedProducerName = formatCapitalizedName(contact.producer_name.display_name);
    if (capitalizedProducerName) return capitalizedProducerName;
    const producerUsername = contact.producer_name.username || '';
    return producerUsername ? producerUsername.charAt(0).toUpperCase() + producerUsername.slice(1) : 'Unassigned';
  }
  const capitalizedOwnName = formatCapitalizedName(contact.display_name);
  if (capitalizedOwnName) return capitalizedOwnName;
  const ownUsername = contact.username || '';
  return ownUsername ? ownUsername.charAt(0).toUpperCase() + ownUsername.slice(1) : 'Unassigned';
};

const buildContactFieldValues = (contact = {}) => ({
  first_name: contact.first_name || '',
  last_name: contact.last_name || '',
  email: contact.email || '',
  companyName: contact.company || '',
  companyId: contact.companies_id ?? contact.company_id ?? null,
  job_title: contact.job_title || '',
  contact_status: contact.contact_status ?? '',
  rating: contact.rating ?? '',
  location: contact.location || '',
  country: contact.country || '',
  mailing_state: contact.mailing_state || '',
  mailing_zip: contact.mailing_zip || '',
  mailing_street: contact.mailing_street || '',
  due_date: normalizeDateInputValue(contact.due_date),
  zoho_due_date_note: contact.zoho_due_date_note || '',
  tags: parseTagsArray(contact.tags),
  assignmentId: contact.user_id ?? contact.producer_name?.id ?? null
});

const COMPANY_ADDRESS_FIELD_MAP = {
  location: 'location',
  country: 'country',
  state: 'mailing_state',
  zip_code: 'mailing_zip',
  address: 'mailing_street'
};

const buildAddressPrefillFromCompany = (company = {}) => {
  if (!company || typeof company !== 'object') {
    return {};
  }
  return Object.entries(COMPANY_ADDRESS_FIELD_MAP).reduce((acc, [companyKey, contactKey]) => {
    const rawValue =
      company && Object.prototype.hasOwnProperty.call(company, companyKey)
        ? company[companyKey]
        : undefined;
    if (rawValue === undefined || rawValue === null) {
      return acc;
    }
    const normalizedValue =
      typeof rawValue === 'string' ? rawValue.trim() : String(rawValue).trim();
    if (!normalizedValue) {
      return acc;
    }
    acc[contactKey] = normalizedValue;
    return acc;
  }, {});
};

const ContactProfileModal = ({
  contact,
  profileData,
  loading,
  error,
  onClose,
  onContactUpdate,
  onRequestCreateCompany,
  currentUserId,
  currentUserRole = '',
  producers = [],
  onShowAllNotes = null
}) => {
  const sourceContact = profileData?.contact || contact || null;
  const [localContact, setLocalContact] = useState(sourceContact);
  const [editValues, setEditValues] = useState(buildContactFieldValues(sourceContact || {}));
  const [activeField, setActiveField] = useState(null);
  const [savingField, setSavingField] = useState(null);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [companySearch, setCompanySearch] = useState('');
  const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false);
  const [companySearchLoading, setCompanySearchLoading] = useState(false);
  const [newStatusEntry, setNewStatusEntry] = useState('');
  const [tagInputValue, setTagInputValue] = useState('');
  const [pendingCompanyAddress, setPendingCompanyAddress] = useState(null);
  const companyDropdownRef = useRef(null);
  const companySearchTimeoutRef = useRef(null);

  const engagement = profileData?.engagement || {
    campaigns: 0,
    totalEmails: 0,
    sent: 0,
    opened: 0,
    clicked: 0,
    replied: 0,
    lastEngagedAt: null
  };

  const prevContactIdRef = useRef(sourceContact?.id ?? null);
  useEffect(() => {
    const isSameContact = sourceContact?.id === prevContactIdRef.current;
    prevContactIdRef.current = sourceContact?.id ?? null;
    setLocalContact(sourceContact);
    setEditValues(buildContactFieldValues(sourceContact || {}));
    if (!isSameContact) {
      setActiveField(null);
      setCompanyDropdownOpen(false);
      setSavingField(null);
      setPendingCompanyAddress(null);
    }
  }, [sourceContact]);

  const contactForInsights = localContact || {};
  const firstName = contactForInsights.first_name || '';
  const lastName = contactForInsights.last_name || '';

  const initials = useMemo(() => {
    const first = firstName?.[0] || '';
    const last = lastName?.[0] || '';
    return (first + last).toUpperCase() || 'NA';
  }, [firstName, lastName]);

  const contactDisplayName = useMemo(() => {
    if (!localContact) return '';
    const nameParts = [localContact.first_name, localContact.last_name]
      .map((part) => part?.trim())
      .filter(Boolean);
    if (nameParts.length) {
      return nameParts.join(' ');
    }
    if (localContact?.company) {
      return localContact.company;
    }
    return localContact?.email || 'Contact';
  }, [localContact]);

  useEffect(() => {
    if (!companyDropdownOpen) return;
    const handleClickOutside = (event) => {
      if (companyDropdownRef.current && !companyDropdownRef.current.contains(event.target)) {
        setCompanyDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [companyDropdownOpen]);

  const fetchCompanyOptions = useCallback(async (searchValue = '') => {
    try {
      setCompanySearchLoading(true);
      const params = new URLSearchParams();
      if (searchValue?.trim()) {
        params.append('search', searchValue.trim());
      }
      params.append('limit', '25');
      const response = await axios.get(`/api/contact-management/companies?${params.toString()}`);
      if (response.data.success) {
        setCompanyOptions(response.data.companies || []);
      }
    } catch (err) {
      console.error('Failed to load company suggestions:', err);
    } finally {
      setCompanySearchLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeField !== 'company') return;
    if (companySearchTimeoutRef.current) {
      clearTimeout(companySearchTimeoutRef.current);
      companySearchTimeoutRef.current = null;
    }
    companySearchTimeoutRef.current = setTimeout(() => {
      fetchCompanyOptions(companySearch);
      companySearchTimeoutRef.current = null;
    }, 300);

    return () => {
      if (companySearchTimeoutRef.current) {
        clearTimeout(companySearchTimeoutRef.current);
        companySearchTimeoutRef.current = null;
      }
    };
  }, [companySearch, activeField, fetchCompanyOptions]);

  useEffect(() => {
    return () => {
      if (companySearchTimeoutRef.current) {
        clearTimeout(companySearchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (activeField !== 'company') {
      setCompanyDropdownOpen(false);
    }
  }, [activeField]);

  useEffect(() => {
    if (activeField !== 'contact_status') {
      setNewStatusEntry('');
    }
  }, [activeField]);

  useEffect(() => {
    if (activeField !== 'tags') {
      setTagInputValue('');
    }
  }, [activeField]);

  const getDisplayValue = (fieldKey) => {
    if (!localContact) return '';
    switch (fieldKey) {
      case 'first_name':
        return localContact.first_name || '';
      case 'last_name':
        return localContact.last_name || '';
      case 'email':
        return localContact.email || '';
      case 'company':
        return localContact.company || '';
      case 'job_title':
        return localContact.job_title || '';
      case 'contact_status': {
        const statuses = parseContactStatusList(localContact.contact_status);
        return statuses.length ? statuses : [];
      }
      case 'rating':
        return localContact.rating ?? '';
      case 'assignment':
        return getAssignmentDisplayName(localContact);
      case 'location':
        return localContact.location || '';
      case 'country':
        return localContact.country || '';
      case 'mailing_state':
        return localContact.mailing_state || '';
      case 'mailing_zip':
        return localContact.mailing_zip || '';
      case 'mailing_street':
        return localContact.mailing_street || '';
      case 'due_date':
        return localContact.due_date ? formatDateOnly(localContact.due_date) : '';
      case 'zoho_due_date_note':
        return localContact.zoho_due_date_note || '';
      case 'tags':
        return parseTagsArray(localContact.tags);
      default:
        return '';
    }
  };

  const getEditValue = (fieldKey) => {
    switch (fieldKey) {
      case 'company':
        return editValues.companyName;
      case 'contact_status':
        return editValues.contact_status ?? '';
      case 'rating':
        return editValues.rating ?? '';
      case 'assignment':
        return editValues.assignmentId ?? '';
      default:
        return editValues[fieldKey] ?? '';
    }
  };

  const normalizedUserRole = (currentUserRole || '').toLowerCase();
  const canEditContact = Boolean(localContact?.id) && (!currentUserId || localContact.user_id === currentUserId);
  const canEditAssignment = canEditContact && normalizedUserRole === 'producer';

  const handleStartEdit = (fieldKey) => {
    if (!localContact?.id || savingField || !canEditContact) return;
    if (fieldKey === 'assignment' && !canEditAssignment) return;
    setActiveField(fieldKey);
    if (fieldKey === 'company') {
      const initialSearch = getEditValue('company') || '';
      setCompanySearch(initialSearch);
      setCompanyDropdownOpen(true);
      fetchCompanyOptions(initialSearch);
    }
  };

  const handleFieldChange = (fieldKey, value) => {
    setEditValues((prev) => {
      if (fieldKey === 'company') {
        return { ...prev, companyName: value, companyId: null };
      }
      if (fieldKey === 'contact_status') {
        return { ...prev, contact_status: value };
      }
      if (fieldKey === 'rating') {
        return { ...prev, rating: value };
      }
      if (fieldKey === 'assignment') {
        if (!canEditAssignment) {
          return prev;
        }
        const nextValue = value === '' ? null : Number(value);
        return {
          ...prev,
          assignmentId: nextValue === null || Number.isNaN(nextValue) ? null : nextValue
        };
      }
      return { ...prev, [fieldKey]: value };
    });
    if (fieldKey === 'company') {
      setCompanySearch(value);
      setCompanyDropdownOpen(true);
      setPendingCompanyAddress(null);
    }
  };

  const updateContactStatusValue = (statuses) => {
    const unique = Array.from(new Set(statuses.filter(Boolean)));
    let value = '';
    if (unique.length === 1) {
      value = unique[0];
    } else if (unique.length > 1) {
      value = JSON.stringify(unique);
    }
    handleFieldChange('contact_status', value);
  };

  const handleAddTag = () => {
    const trimmed = tagInputValue.trim();
    if (!trimmed) return;
    const existing = Array.isArray(editValues.tags) ? editValues.tags : [];
    if (existing.includes(trimmed)) return;
    const newTags = [...existing, trimmed];
    setEditValues((prev) => ({ ...prev, tags: newTags }));
    setTagInputValue('');
    handleConfirmField('tags', newTags, true);
  };

  const handleRemoveTag = (tagToRemove) => {
    const existing = Array.isArray(editValues.tags) ? editValues.tags : [];
    const newTags = existing.filter((tag) => tag !== tagToRemove);
    setEditValues((prev) => ({ ...prev, tags: newTags }));
    handleConfirmField('tags', newTags, true);
  };

  const handleTagInputKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddTag();
    }
  };

  const handleShowAllNotes = () => {
    if (!localContact?.id || !canShowAllNotes) return;
    const latestNoteContent =
      (Array.isArray(notes) && notes.length ? notes[0]?.content : null) ??
      localContact.notes ??
      '';
    onShowAllNotes(localContact.id, contactType, latestNoteContent);
  };

  const fetchCompanyAddressDetails = async (companyId) => {
    if (!companyId) return;
    try {
      const response = await axios.get(`/api/contact-management/companies/${companyId}`);
      if (response.data.success && response.data.company) {
        const addressPrefill = buildAddressPrefillFromCompany(response.data.company);
        if (Object.keys(addressPrefill).length) {
          setEditValues((prev) => ({
            ...prev,
            ...addressPrefill
          }));
          setPendingCompanyAddress(addressPrefill);
        }
      }
    } catch (err) {
      console.error('Failed to load company details for address auto-fill:', err);
    }
  };

  const handleSelectCompany = (companyOption) => {
    if (!companyOption) return;
    const addressPrefill = buildAddressPrefillFromCompany(companyOption);
    const hasPrefill = Object.keys(addressPrefill).length > 0;
    setEditValues((prev) => ({
      ...prev,
      ...addressPrefill,
      companyName: companyOption.name,
      companyId: companyOption.id
    }));
    setPendingCompanyAddress(hasPrefill ? addressPrefill : null);
    setCompanySearch(companyOption.name);
    setCompanyDropdownOpen(false);
    if (!hasPrefill) {
      fetchCompanyAddressDetails(companyOption.id);
    }
  };

  const handleUseNewCompany = () => {
    if (!canEditContact) return;
    const trimmedName = companySearch.trim();
    if (onRequestCreateCompany) {
      setCompanyDropdownOpen(false);
      onRequestCreateCompany(trimmedName, (newCompany) => {
        if (newCompany) {
          handleSelectCompany(newCompany);
        }
      });
      return;
    }

    setEditValues((prev) => ({
      ...prev,
      companyName: trimmedName,
      companyId: null
    }));
    setPendingCompanyAddress(null);
    setCompanyDropdownOpen(false);
  };

  const handleCancelEdit = () => {
    setEditValues(buildContactFieldValues(localContact || {}));
    setActiveField(null);
    setCompanyDropdownOpen(false);
    setPendingCompanyAddress(null);
  };

  const handleConfirmField = async (fieldKey, tagsOverride, keepOpen = false) => {
    if (!localContact?.id || !canEditContact) return;
    const contactType = localContact.contact_type || contact?.contact_type || profileData?.contact?.contact_type || 'contact';
    const payload = {};
    let contactPatch = {};

    const existingEmail = (editValues.email || localContact.email || '').trim();
    const currentCompanyId =
      editValues.companyId ??
      localContact.companies_id ??
      localContact.company_id ??
      null;
    const currentCompanyName =
      editValues.companyName ??
      localContact.company ??
      '';

    if (fieldKey === 'first_name' || fieldKey === 'last_name') {
      const nextFirst = (editValues.first_name || '').trim();
      const nextLast = (editValues.last_name || '').trim();
      if (!nextFirst && !nextLast) {
        alert('Name cannot be empty.');
        return;
      }
      if (!existingEmail) {
        alert('Email is required to update contact details.');
        return;
      }
      payload.first_name = nextFirst;
      payload.last_name = nextLast;
      payload.email = existingEmail;
      payload.company_id = currentCompanyId;
      payload.company_name = currentCompanyName;
      contactPatch = { first_name: nextFirst, last_name: nextLast };
    } else if (fieldKey === 'email') {
      const nextEmail = (editValues.email || '').trim();
      if (!nextEmail) {
        alert('Email cannot be empty.');
        return;
      }
      payload.email = nextEmail;
      payload.company_id = currentCompanyId;
      payload.company_name = currentCompanyName;
      contactPatch = { email: nextEmail };
    } else if (fieldKey === 'job_title') {
      if (!existingEmail) {
        alert('Email is required to update contact details.');
        return;
      }
      payload.job_title = editValues.job_title || '';
      payload.email = existingEmail;
      payload.company_id = currentCompanyId;
      payload.company_name = currentCompanyName;
      contactPatch = { job_title: payload.job_title };
    } else if (fieldKey === 'company') {
      if (!existingEmail) {
        alert('Email is required to update contact details.');
        return;
      }
      payload.company_name = editValues.companyName || '';
      payload.company_id = editValues.companyId ?? null;
      payload.email = existingEmail;
      const addressUpdates =
        pendingCompanyAddress && Object.keys(pendingCompanyAddress).length
          ? pendingCompanyAddress
          : null;
      if (addressUpdates) {
        Object.entries(addressUpdates).forEach(([key, value]) => {
          payload[key] = value;
        });
      }
      contactPatch = {
        company: payload.company_name,
        company_id: payload.company_id,
        companies_id: payload.company_id,
        ...(addressUpdates || {})
      };
    } else if (fieldKey === 'contact_status') {
      if (!existingEmail) {
        alert('Email is required to update contact details.');
        return;
      }
      const nextStatus = normalizeContactStatusInput(editValues.contact_status);
      payload.contact_status = nextStatus;
      payload.email = existingEmail;
      payload.company_id = currentCompanyId;
      payload.company_name = currentCompanyName;
      contactPatch = { contact_status: nextStatus };
    } else if (fieldKey === 'rating') {
      if (!existingEmail) {
        alert('Email is required to update contact details.');
        return;
      }
      const rawRating = editValues.rating;
      const parsedRating =
        rawRating === '' || rawRating === null || rawRating === undefined
          ? null
          : Number(rawRating);
      if (parsedRating !== null && Number.isNaN(parsedRating)) {
        alert('Rating must be a number.');
        return;
      }
      payload.rating = parsedRating;
      payload.email = existingEmail;
      payload.company_id = currentCompanyId;
      payload.company_name = currentCompanyName;
      contactPatch = { rating: parsedRating };
    } else if (fieldKey === 'due_date') {
      if (!existingEmail) {
        alert('Email is required to update contact details.');
        return;
      }
      const rawDueDate = (editValues.due_date || '').trim();
      const normalizedDueDate = rawDueDate || null;
      payload.due_date = normalizedDueDate;
      payload.email = existingEmail;
      payload.company_id = currentCompanyId;
      payload.company_name = currentCompanyName;
      contactPatch = { due_date: normalizedDueDate, zoho_due_date: normalizedDueDate };
    } else if (fieldKey === 'zoho_due_date_note') {
      if (!existingEmail) {
        alert('Email is required to update contact details.');
        return;
      }
      const nextNote = editValues.zoho_due_date_note || '';
      payload.zoho_due_date_note = nextNote;
      payload.email = existingEmail;
      payload.company_id = currentCompanyId;
      payload.company_name = currentCompanyName;
      contactPatch = { zoho_due_date_note: nextNote };
    } else if (fieldKey === 'assignment') {
      if (!canEditAssignment) {
        alert('Only producers can change assignment.');
        return;
      }
      if (!existingEmail) {
        alert('Email is required to update contact details.');
        return;
      }
      const nextAssignmentId =
        editValues.assignmentId === undefined || editValues.assignmentId === null
          ? null
          : Number(editValues.assignmentId);
      if (nextAssignmentId === null || Number.isNaN(nextAssignmentId)) {
        alert('Please select a team member to assign this contact.');
        return;
      }
      const selectedProducer = producers.find(
        (producer) => producer.id === nextAssignmentId
      );
      const normalizedProducer = selectedProducer
        ? {
            id: selectedProducer.id,
            display_name: selectedProducer.display_name || null,
            username: selectedProducer.username || null
          }
        : null;
      payload.producer_name = nextAssignmentId;
      payload.email = existingEmail;
      payload.company_id = currentCompanyId;
      payload.company_name = currentCompanyName;
      contactPatch = {
        user_id: nextAssignmentId,
        producer_name: normalizedProducer,
        display_name:
          normalizedProducer?.display_name ||
          normalizedProducer?.username ||
          null,
        username: normalizedProducer?.username || null
      };
    } else if (fieldKey === 'tags') {
      if (!existingEmail) {
        alert('Email is required to update contact details.');
        return;
      }
      const cleanedTags = Array.isArray(tagsOverride ?? editValues.tags)
        ? (tagsOverride ?? editValues.tags).map((tag) => tag.trim()).filter(Boolean)
        : [];
      payload.tags = serializeTagsArray(cleanedTags);
      payload.email = existingEmail;
      payload.company_id = currentCompanyId;
      payload.company_name = currentCompanyName;
      contactPatch = { tags: cleanedTags };
    } else if (['location', 'country', 'mailing_state', 'mailing_zip', 'mailing_street'].includes(fieldKey)) {
      if (!existingEmail) {
        alert('Email is required to update contact details.');
        return;
      }
      const value = editValues[fieldKey] ?? '';
      payload[fieldKey] = value;
      payload.email = existingEmail;
      payload.company_id = currentCompanyId;
      payload.company_name = currentCompanyName;
      contactPatch = { [fieldKey]: value };
    } else {
      return;
    }

    setSavingField(fieldKey);
    try {
      const response = await axios.put(
        `/api/contact-management/contacts/${localContact.id}/info?contactType=${contactType}`,
        payload
      );
      if (response.data.success) {
        setLocalContact((prev) => {
          const updated = { ...(prev || {}), ...contactPatch };
          setEditValues(buildContactFieldValues(updated));
          if (onContactUpdate) {
            onContactUpdate(updated);
          }
          return updated;
        });
        setPendingCompanyAddress(null);
        if (!keepOpen) setActiveField(null);
        setCompanyDropdownOpen(false);
      } else {
        throw new Error(response.data.error || 'Failed to update contact.');
      }
    } catch (err) {
      console.error('Failed to update contact:', err);
      alert(err.response?.data?.error || 'Failed to update contact. Please try again.');
    } finally {
      setSavingField(null);
    }
  };

  const handleEditingKeyDown = (event, fieldKey) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleConfirmField(fieldKey);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      handleCancelEdit();
    }
  };

  if (!localContact) {
    return null;
  }

  const tags = Array.isArray(localContact.tags)
    ? localContact.tags
    : (() => {
        try {
          return localContact.tags ? JSON.parse(localContact.tags) : [];
        } catch (err) {
          return [];
        }
      })();

  const contactFields = [
    { key: 'first_name', label: 'First Name', placeholder: 'Jane', type: 'text' },
    { key: 'last_name', label: 'Last Name', placeholder: 'Doe', type: 'text' },
    { key: 'email', label: 'Email', placeholder: 'name@email.com', type: 'email' },
    { key: 'assignment', label: 'Assigned To', type: 'assignment' },
  { key: 'company', label: 'Company', placeholder: 'Search companies', type: 'text' },
  { key: 'job_title', label: 'Title', placeholder: 'Head of Growth', type: 'text' },
  { key: 'contact_status', label: 'Contact Status', type: 'status-multi' },
  { key: 'rating', label: 'Rating', placeholder: 'e.g. 4', type: 'number' },
  { key: 'due_date', label: 'Due Date', placeholder: 'Select date', type: 'date' },
  {
    key: 'zoho_due_date_note',
    label: 'All Due Dates',
    placeholder: 'Add due date notes',
    type: 'textarea',
    rows: 3
  }
];

  const addressFields = [
    { key: 'location', label: 'Location', placeholder: 'San Francisco, CA' },
    { key: 'country', label: 'Country', placeholder: 'United States' },
    { key: 'mailing_state', label: 'Mailing State/Province', placeholder: 'CA' },
    { key: 'mailing_zip', label: 'Mailing Postal Code', placeholder: '94105' },
    { key: 'mailing_street', label: 'Mailing Street', placeholder: '123 Main St Suite 100' }
  ];

  const isAnyEditing = Boolean(activeField);
  const notes = profileData?.notes || [];
  const createdAtDisplay = formatDateOnly(localContact.created_at);
  const canShowAllNotes = typeof onShowAllNotes === 'function';
  const contactType =
    localContact.contact_type ||
    contact?.contact_type ||
    profileData?.contact?.contact_type ||
    'contact';

const renderFieldGrid = (fields) => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {fields.map((field) => {
        const isActive = activeField === field.key;
        const displayValue = getDisplayValue(field.key);
        const isAssignmentField = field.key === 'assignment';
        const assignmentLocked = isAssignmentField && (!canEditAssignment || producers.length === 0);
        const startDisabled =
          !canEditContact ||
          ((isAnyEditing || savingField) && !isActive) ||
          loading ||
          assignmentLocked;
        const isSavingCurrent = savingField === field.key;

        return (
          <div key={field.key} className="p-3 border border-gray-100 rounded-xl">
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
              {field.label}
            </p>
            {isActive ? (
              field.key === 'company' ? (
                <div className="mt-2 space-y-2">
                  <div className="relative" ref={companyDropdownRef}>
                    <input
                      type="text"
                      value={companySearch}
                      onChange={(e) => handleFieldChange('company', e.target.value)}
                      onKeyDown={(e) => handleEditingKeyDown(e, 'company')}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                      placeholder={field.placeholder}
                      autoFocus
                      disabled={isSavingCurrent}
                    />
                    {companyDropdownOpen && (
                      <div className="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                        {companySearchLoading ? (
                          <p className="px-3 py-2 text-sm text-gray-500">Searching...</p>
                        ) : (
                          <>
                            {companyOptions.length === 0 && (
                              <p className="px-3 py-2 text-sm text-gray-500">No matches</p>
                            )}
                            {companyOptions.map((option) => (
                              <button
                                type="button"
                                key={option.id}
                                onClick={() => handleSelectCompany(option)}
                                className="w-full px-3 py-2 text-left text-sm hover:bg-indigo-50"
                              >
                                {option.name}
                              </button>
                            ))}
                          </>
                        )}
                        <button
                          type="button"
                          onClick={handleUseNewCompany}
                          className="w-full px-3 py-2 text-left text-sm font-semibold text-indigo-600 hover:bg-indigo-50 border-t border-gray-100"
                          disabled={!companySearch.trim()}
                        >
                          + New Company
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => handleConfirmField('company')}
                      className="p-2 rounded-full bg-emerald-50 text-emerald-600 hover:bg-emerald-100 disabled:opacity-50"
                      disabled={isSavingCurrent}
                      title="Save company"
                    >
                      <Check size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      className="p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50"
                      disabled={isSavingCurrent}
                      title="Cancel"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              ) : field.key === 'assignment' ? (
                (() => {
                  const assignmentValue = getEditValue('assignment');
                  const normalizedValue =
                    assignmentValue === null || assignmentValue === '' ? '' : String(assignmentValue);
                  const assignmentExists =
                    normalizedValue !== '' &&
                    producers.some((producer) => String(producer.id) === normalizedValue);

                  return (
                    <div className="mt-2 flex items-center gap-2">
                      <select
                        value={normalizedValue}
                        onChange={(e) => handleFieldChange('assignment', e.target.value)}
                        disabled={
                          isSavingCurrent ||
                          !canEditAssignment ||
                          producers.length === 0
                        }
                        className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-50"
                      >
                        <option value="">{producers.length ? 'Select producer' : 'No producers available'}</option>
                        {!assignmentExists && normalizedValue !== '' && (
                          <option value={normalizedValue}>{getAssignmentDisplayName(localContact)} (current)</option>
                        )}
                        {producers.map((producer) => (
                          <option key={producer.id} value={producer.id}>
                            {producer.display_name || producer.username || `User ${producer.id}`}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => handleConfirmField('assignment')}
                        className="p-2 rounded-full bg-emerald-50 text-emerald-600 hover:bg-emerald-100 disabled:opacity-50"
                        disabled={
                          isSavingCurrent ||
                          !canEditAssignment ||
                          producers.length === 0
                        }
                        title="Save assignment"
                      >
                        <Check size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={handleCancelEdit}
                        className="p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50"
                        disabled={isSavingCurrent}
                        title="Cancel"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  );
                })()
              ) : field.type === 'status-multi' ? (
                (() => {
                  const selectedStatuses = parseContactStatusList(editValues.contact_status);
                  const toggleStatusSelection = (statusLabel) => {
                    const isSelected = selectedStatuses.includes(statusLabel);
                    const next = isSelected
                      ? selectedStatuses.filter((value) => value !== statusLabel)
                      : [...selectedStatuses, statusLabel];
                    updateContactStatusValue(next);
                  };

                  const handleRemoveStatus = (statusLabel) => {
                    const next = selectedStatuses.filter((value) => value !== statusLabel);
                    updateContactStatusValue(next);
                  };

                  const handleAddCustomStatus = () => {
                    const trimmed = newStatusEntry.trim();
                    if (!trimmed) return;
                    if (!selectedStatuses.includes(trimmed)) {
                      updateContactStatusValue([...selectedStatuses, trimmed]);
                    }
                    setNewStatusEntry('');
                  };

                  const handleStatusInputKeyDown = (event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      handleAddCustomStatus();
                    }
                  };

                  return (
                    <div className="mt-2 space-y-3">
                      <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto divide-y">
                        {CONTACT_STATUS_CHOICES.map((option) => {
                          const checked = selectedStatuses.includes(option);
                          return (
                            <label
                              key={option}
                              className="flex items-center justify-between px-3 py-2 text-sm cursor-pointer hover:bg-indigo-50"
                            >
                              <div className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleStatusSelection(option)}
                                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                  disabled={isSavingCurrent}
                                />
                                <span className="text-gray-700">{option}</span>
                              </div>
                            </label>
                          );
                        })}
                      </div>

                      <div>
                        <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
                          Selected
                        </p>
                        {selectedStatuses.length ? (
                          <div className="flex flex-wrap gap-2">
                            {selectedStatuses.map((status) => (
                              <span
                                key={status}
                                className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100"
                              >
                                {status}
                                <button
                                  type="button"
                                  onClick={() => handleRemoveStatus(status)}
                                  className="text-indigo-500 hover:text-indigo-700"
                                  disabled={isSavingCurrent}
                                  title="Remove status"
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-400 italic">No statuses selected</p>
                        )}
                      </div>

                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={newStatusEntry}
                            onChange={(e) => setNewStatusEntry(e.target.value)}
                            onKeyDown={handleStatusInputKeyDown}
                            className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                            placeholder="Add custom status"
                            disabled={isSavingCurrent}
                          />
                          <button
                            type="button"
                            onClick={handleAddCustomStatus}
                            className="px-3 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                            disabled={isSavingCurrent || !newStatusEntry.trim()}
                          >
                            Add
                          </button>
                        </div>
                        <p className="text-[11px] text-gray-500">
                          Use the checkboxes or add your own custom status label.
                        </p>
                      </div>

                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => handleConfirmField(field.key)}
                          className="p-2 rounded-full bg-emerald-50 text-emerald-600 hover:bg-emerald-100 disabled:opacity-50"
                          disabled={isSavingCurrent}
                          title="Save"
                        >
                          <Check size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={handleCancelEdit}
                          className="p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50"
                          disabled={isSavingCurrent}
                          title="Cancel"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </div>
                  );
                })()
              ) : field.type === 'textarea' ? (
                <div className="mt-2 space-y-2">
                  <textarea
                    value={getEditValue(field.key)}
                    onChange={(e) => handleFieldChange(field.key, e.target.value)}
                    onKeyDown={(e) => handleEditingKeyDown(e, field.key)}
                    rows={field.rows || 3}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                    placeholder={field.placeholder}
                    autoFocus
                    disabled={isSavingCurrent}
                  />
                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => handleConfirmField(field.key)}
                      className="p-2 rounded-full bg-emerald-50 text-emerald-600 hover:bg-emerald-100 disabled:opacity-50"
                      disabled={isSavingCurrent}
                      title="Save"
                    >
                      <Check size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      className="p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50"
                      disabled={isSavingCurrent}
                      title="Cancel"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-2 flex items-center gap-2">
                  <input
                    type={field.type}
                    value={getEditValue(field.key)}
                    onChange={(e) => handleFieldChange(field.key, e.target.value)}
                    onKeyDown={(e) => handleEditingKeyDown(e, field.key)}
                    className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                    placeholder={field.placeholder}
                    autoFocus
                    disabled={isSavingCurrent}
                  />
                  <button
                    type="button"
                    onClick={() => handleConfirmField(field.key)}
                    className="p-2 rounded-full bg-emerald-50 text-emerald-600 hover:bg-emerald-100 disabled:opacity-50"
                    disabled={isSavingCurrent}
                    title="Save"
                  >
                    <Check size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50"
                    disabled={isSavingCurrent}
                    title="Cancel"
                  >
                    <X size={16} />
                  </button>
                </div>
              )
            ) : (
              <button
                type="button"
                onClick={() => handleStartEdit(field.key)}
                disabled={startDisabled}
                className={`group mt-1 w-full flex items-center justify-between gap-2 rounded-lg border border-transparent px-2 py-1 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
                  startDisabled ? 'cursor-not-allowed opacity-50' : 'hover:border-gray-200'
                }`}
              >
                {field.key === 'contact_status' ? (
                  <span className="flex-1">
                    {Array.isArray(displayValue) && displayValue.length ? (
                      <span className="flex flex-wrap gap-1">
                        {displayValue.map((status) => (
                          <span
                            key={status}
                            className="px-2 py-0.5 text-xs rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100"
                          >
                            {status}
                          </span>
                        ))}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400 italic">Not provided</span>
                    )}
                  </span>
                ) : (
                  <span
                    className={`text-sm break-all ${
                      displayValue ? 'text-gray-900' : 'text-gray-400 italic'
                    }`}
                  >
                    {displayValue || 'Not provided'}
                  </span>
                )}
                <Edit3
                  size={14}
                  className={`text-gray-400 transition-opacity ${
                    startDisabled ? 'opacity-30' : 'opacity-0 group-hover:opacity-100'
                  }`}
                />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );

  const renderTagsSection = () => {
    const displayTags = parseTagsArray(localContact.tags);
    const editingTags = Array.isArray(editValues.tags) ? editValues.tags : [];
    const isTagsActive = activeField === 'tags';
    const startDisabled =
      !canEditContact || ((isAnyEditing || savingField) && !isTagsActive) || loading;
    const isSavingTags = savingField === 'tags';

    if (isTagsActive) {
      return (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {editingTags.length ? (
              editingTags.map((tag) => {
                const isBlockTag = tag === 'block';
                const tagLabel = isBlockTag ? 'DNC' : tag;
                return (
                  <span
                    key={tag}
                    className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                      isBlockTag ? 'bg-red-50 text-red-700' : 'bg-indigo-50 text-indigo-700'
                    }`}
                  >
                    {tagLabel}
                    {!isBlockTag && (
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="text-current hover:text-gray-900"
                        disabled={isSavingTags}
                      >
                        ×
                      </button>
                    )}
                  </span>
                );
              })
            ) : (
              <span className="text-sm text-gray-400 italic">No tags added yet</span>
            )}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={tagInputValue}
              onChange={(e) => setTagInputValue(e.target.value)}
              onKeyDown={handleTagInputKeyDown}
              className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              placeholder="Add a tag"
              disabled={isSavingTags}
              autoFocus
            />
            <button
              type="button"
              onClick={handleAddTag}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50"
              disabled={isSavingTags || !tagInputValue.trim()}
            >
              Add
            </button>
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleCancelEdit}
              className="px-3 py-1 text-sm text-gray-500 hover:text-gray-700"
            >
              Done
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-between">
        <div className="flex flex-wrap gap-2 flex-1">
          {displayTags.length ? (
            displayTags.map((tag) => {
              const isBlockTag = tag === 'block';
              const tagLabel = isBlockTag ? 'DNC' : tag;
              return (
                <span
                  key={tag}
                  className={`px-3 py-1 rounded-full text-xs font-medium ${
                    isBlockTag ? 'bg-red-50 text-red-700' : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {tagLabel}
                </span>
              );
            })
          ) : (
            <span className="text-sm text-gray-400 italic">No tags available</span>
          )}
        </div>
        {canEditContact && (
          <button
            type="button"
            onClick={() => handleStartEdit('tags')}
            disabled={startDisabled}
            className={`ml-4 inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm font-semibold border transition ${
              startDisabled
                ? 'cursor-not-allowed opacity-50 border-gray-200 text-gray-400'
                : 'border-gray-200 text-gray-600 hover:border-gray-300'
            }`}
          >
            <Edit3 size={14} />
            Edit
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 backdrop-blur-sm overflow-y-auto py-8 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl overflow-hidden">
        <div className="relative">
          <div className="bg-gradient-to-r from-indigo-600 via-purple-500 to-pink-500 text-white p-6">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-white/80 hover:text-white"
              aria-label="Close contact profile"
            >
              <X size={20} />
            </button>

            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center text-2xl font-semibold">
                  {initials}
                </div>
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    {localContact.status && (
                      <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-white/20">
                        {localContact.status?.toUpperCase()}
                      </span>
                    )}
                    {localContact.source && (
                      <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-white/10 border border-white/25 text-white/90">
                        {localContact.source}
                      </span>
                    )}
                  </div>
                  <p className="mt-4 text-xs text-white/80">
                    Created at {createdAtDisplay}
                  </p>
                </div>
              </div>
              <div className="flex flex-col items-end text-right min-w-[180px] mt-6 md:mt-0">
                <div className="bg-white/90 rounded-2xl px-5 py-4 border border-white/40 shadow-lg text-gray-900">
                  <p className="text-xs uppercase tracking-wide text-gray-500">Engagement</p>
                  <p className="text-3xl font-semibold text-indigo-600">
                    {(engagement.clicked || 0) > 0 ? '7.5/10' : '5.0/10'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Momentum score</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="py-16 text-center text-gray-500">
            <RefreshCw className="animate-spin mx-auto mb-4" size={32} />
            Loading contact insights...
          </div>
        ) : error ? (
          <div className="py-12 text-center text-red-500">{error}</div>
        ) : (
          <div className="p-6 space-y-6">
            <div className="bg-white border border-gray-100 rounded-2xl p-5 space-y-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-900">Contact Details</h3>
              </div>
              {renderFieldGrid(contactFields)}
            </div>

            <div className="bg-white border border-gray-100 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-900">Tags</h3>
                {!canEditContact && <span className="text-xs text-gray-400">Read-only</span>}
              </div>
              {renderTagsSection()}
            </div>

            <div className="bg-white border border-gray-100 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-900">Address Information</h3>
                {!canEditContact && <span className="text-xs text-gray-400">Read-only</span>}
              </div>
              {renderFieldGrid(addressFields)}
            </div>

            <div className="bg-white border border-gray-100 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-900">Engagement Metrics</h3>
              </div>
              <div className="flex flex-wrap lg:flex-nowrap gap-4">
                <div className="flex-1 min-w-[140px] p-3 rounded-xl bg-indigo-50 border border-indigo-100">
                  <p className="text-xs text-indigo-600 uppercase">Emails Sent</p>
                  <p className="text-2xl font-semibold text-indigo-900">{engagement.totalEmails}</p>
                </div>
                <div className="flex-1 min-w-[140px] p-3 rounded-xl bg-purple-50 border border-purple-100">
                  <p className="text-xs text-purple-600 uppercase">Opened</p>
                  <p className="text-2xl font-semibold text-purple-900">{engagement.opened}</p>
                </div>
                <div className="flex-1 min-w-[140px] p-3 rounded-xl bg-pink-50 border border-pink-100">
                  <p className="text-xs text-pink-600 uppercase">Clicked</p>
                  <p className="text-2xl font-semibold text-pink-900">{engagement.clicked}</p>
                </div>
                <div className="flex-1 min-w-[140px] p-3 rounded-xl bg-emerald-50 border border-emerald-100">
                  <p className="text-xs text-emerald-600 uppercase">Replied</p>
                  <p className="text-2xl font-semibold text-emerald-900">{engagement.replied}</p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-100 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-900">Recent Notes</h3>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handleShowAllNotes}
                    disabled={!canShowAllNotes}
                    className={`text-xs font-semibold ${
                      canShowAllNotes
                        ? 'text-indigo-600 hover:text-indigo-700'
                        : 'text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    Show All
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (!localContact?.id || !canShowAllNotes) return;
                      const latestNoteContent = (Array.isArray(notes) && notes.length ? notes[0]?.content : null) ?? localContact.notes ?? '';
                      onShowAllNotes(localContact.id, contactType, latestNoteContent, true);
                    }}
                    disabled={!canShowAllNotes}
                    className={`text-xs font-semibold px-2 py-1 rounded-lg border transition ${
                      canShowAllNotes
                        ? 'border-indigo-200 text-indigo-600 hover:bg-indigo-50'
                        : 'border-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    + New Note
                  </button>
                </div>
              </div>
              <div className="space-y-3">
                {notes.length === 0 && (
                  <p className="text-sm text-gray-500">No notes recorded yet.</p>
                )}
                {notes.map((note) => (
                  <div key={note.id} className="border border-gray-100 rounded-xl p-3">
                    <p className="text-sm text-gray-800">{normalizeNoteContent(note.content)}</p>
                    <p className="text-[11px] text-gray-400 mt-1">{formatDate(note.created_at)}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};
export default ContactProfileModal;
